import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { IconFileInvoice, IconMinus, IconPlus, IconTrash, IconX } from "@tabler/icons-react";
import { clsx } from "clsx";
import { useCartStore } from "../../../../hooks/stores/useCartStore";
import { useCartUiStore } from "../../../../hooks/stores/useCartUiStore";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { Image } from "../../../../components/common/image";
import { Button } from "../../../../components/common/button";
import { Heading, Text } from "../../../../components/common/typography";
import { CurrencyAmount } from "../../../../components/common/currency_amount";
import { IconShoppingCart } from "@tabler/icons-react";
import { EmptyState } from "../../../../components/common/empty-state";
import { getProductLineTotal } from "../../../products/utils/get-pricelist-price";
import { getProductDetailsUrl } from "../../../products/utils/get-product-details-url";
import { useCreateQuotation } from "../../hooks/useCreateQuotation";
import type { QuotationOrderLineDto } from "../../../../api";

/**
 * Shopping cart popover — replaces the old slide-in drawer. Anchored under
 * the cart icon in the top nav (same pattern as the user menu). All state
 * comes from `useCartStore` (items) + `useCartUiStore` (visibility).
 */
export function CartPopover() {
  const navigate = useNavigate();
  const {
    items,
    updateQuantity,
    removeFromCart,
    clearCart,
    getTotalPrice,
  } = useCartStore();
  const { createQuotation, isLoading: isCreatingQuotation } =
    useCreateQuotation({ clearCartOnSuccess: true });

  const isOpen = useCartUiStore((s) => s.isOpen);
  const close = useCartUiStore((s) => s.close);
  const anchor = useCartUiStore((s) => s.cartAnchor);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (anchor?.contains(target)) return;
      close();
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isOpen, close, anchor]);

  if (!isOpen) return null;

  const totalPrice = getTotalPrice();

  const handleCheckout = () => {
    close();
    navigate(AppRoutes.CHECKOUT);
  };

  const handleCreateQuotation = async () => {
    const orderLines: QuotationOrderLineDto[] = items.map((item) => ({
      productId: item.product.id,
      quantity: item.quantity,
      ...(item.warranty ? { warrantyProductId: item.warranty.productId } : {}),
    })) as QuotationOrderLineDto[];
    const quotation = await createQuotation(orderLines);
    if (quotation) close();
  };

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Shopping cart"
      className={clsx(
        "absolute right-0 top-[calc(100%+12px)] z-[80] w-[440px] max-w-[calc(100vw-2rem)]",
        "overflow-hidden rounded-2xl border border-borderDefault bg-bgWhite",
        "shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.15),0px_8px_10px_2px_rgba(0,0,0,0.1)]",
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-borderDefault p-3">
        <Heading variant="h3" tone="dark">
          Shopping Cart
        </Heading>
        <button
          type="button"
          onClick={close}
          aria-label="Close cart"
          className="inline-flex size-6 items-center justify-center text-textDark hover:text-textBody"
        >
          <IconX size={20} />
        </button>
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={IconShoppingCart}
          title="Your cart is empty"
          size="sm"
          primaryAction={{
            label: "Browse products",
            onClick: () => {
              close();
              navigate(AppRoutes.PRODUCTS);
            },
          }}
        />
      ) : (
        <>
          {/* Items */}
          <div className="flex max-h-[420px] flex-col gap-3 overflow-y-auto p-3">
            {items.map((item) => {
              const availableQuantity = Number(item.product.supplierStock ?? 0);
              const isOutOfStock = availableQuantity === 0;
              const canIncrement =
                isOutOfStock || item.quantity < availableQuantity;
              const lineTotal = getProductLineTotal(item.product, item.quantity);

              return (
                <div
                  key={item.product.id}
                  className="flex items-start justify-between gap-3 rounded-[10px] bg-bgLight p-3"
                >
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <Link
                      to={getProductDetailsUrl(
                        item.product.productTemplateId ?? item.product.id,
                        item.product.id,
                      )}
                      onClick={close}
                      aria-label={`View ${item.product.name ?? "product"}`}
                      className="size-16 shrink-0 overflow-hidden rounded-[10px] bg-bgWhite p-2 hover:opacity-90 transition-opacity"
                    >
                      <Image
                        image={item.product.image}
                        alt={item.product.name || "Product"}
                        size="w-full h-full"
                        showPlaceholder
                      />
                    </Link>

                    <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
                      <Link
                        to={getProductDetailsUrl(
                          item.product.productTemplateId ?? item.product.id,
                          item.product.id,
                        )}
                        onClick={close}
                        className="min-w-0 hover:text-brandPrimary transition-colors"
                      >
                        <Text variant="bodySmBold" tone="dark" className="truncate">
                          {item.product.name || "—"}
                        </Text>
                      </Link>
                      {item.product.description ? (
                        <Text variant="bodyXs" tone="body" className="truncate">
                          {item.product.description}
                        </Text>
                      ) : null}

                      <div className="flex items-center justify-between gap-2">
                        <CurrencyAmount
                          amount={lineTotal}
                          currency="QAR"
                          variant="bodyBold"
                          tone="dark"
                        />

                        <div className="flex items-center gap-1.5">
                          <Text variant="bodySm" tone="body" inline>
                            Qty:
                          </Text>
                          <button
                            type="button"
                            onClick={() =>
                              updateQuantity(item.product.id, item.quantity - 1)
                            }
                            aria-label="Decrease quantity"
                            className="inline-flex size-7 items-center justify-center rounded-[10px] border border-borderDefault bg-bgWhite text-textDark hover:border-textDark"
                          >
                            <IconMinus size={14} />
                          </button>
                          <Text
                            variant="bodySm"
                            tone="dark"
                            inline
                            className="min-w-[20px] text-center"
                          >
                            {item.quantity}
                          </Text>
                          <button
                            type="button"
                            onClick={() =>
                              updateQuantity(item.product.id, item.quantity + 1)
                            }
                            disabled={!canIncrement}
                            aria-label="Increase quantity"
                            className="inline-flex size-7 items-center justify-center rounded-[10px] border border-borderDefault bg-bgWhite text-textDark hover:border-textDark disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <IconPlus size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => removeFromCart(item.product.id)}
                    aria-label={`Remove ${item.product.name ?? "item"}`}
                    className="inline-flex size-6 items-center justify-center text-textBody hover:text-accentError"
                  >
                    <IconTrash size={16} />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="flex flex-col gap-3 border-t border-borderDefault bg-bgLight p-3">
            <div className="flex items-center justify-between">
              <Text variant="bodyBold" tone="dark" inline>
                Total
              </Text>
              <CurrencyAmount
                amount={totalPrice}
                currency="QAR"
                variant="bodyLgBold"
                tone="dark"
              />
            </div>

            <Button variant="primary" fullWidth onClick={handleCheckout}>
              View Cart & Checkout
            </Button>

            <Button
              variant="secondary"
              fullWidth
              leftIcon={<IconFileInvoice size={18} />}
              onClick={handleCreateQuotation}
              disabled={isCreatingQuotation}
            >
              {isCreatingQuotation ? "Creating quotation…" : "Request quotation"}
            </Button>

            <div className="flex items-center justify-start">
              <button
                type="button"
                onClick={clearCart}
                className="text-left"
              >
                <Text variant="bodySm" tone="muted" inline>
                  Clear cart
                </Text>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default CartPopover;
